import { useMutation, useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import GeoPointsList from "@/components/geo/GeoPointsList"
import { normalizeGeoPoint } from "@/components/geo/geoPointHelpers"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import { getBossPoints, startBossFight } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function BossMapPage() {
  const [params] = useSearchParams()
  const highlightId = params.get("pointId")
  const gps = usePlayerGpsState()
  const { passport, collectionCards, syncGameState, bumpCards } =
    useGuardiansPassport()

  const pointsQuery = useQuery({
    queryKey: ["boss-points"],
    queryFn: getBossPoints,
  })

  const activeId =
    passport?.activeCardId ?? passport?.activeCard?.id ?? collectionCards[0]?.id

  const fight = useMutation({
    mutationFn: (bossPointId: string) => {
      if (!gps || !activeId) throw new Error("Нужны GPS и активная карта")
      return startBossFight({
        bossPointId,
        cardId: activeId,
        playerLat: gps.lat,
        playerLng: gps.lng,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      void pointsQuery.refetch()
    },
  })

  const points = (pointsQuery.data ?? []).map((p) =>
    normalizeGeoPoint({
      id: p.id,
      name: p.name,
      latitude: p.latitude,
      longitude: p.longitude,
      radiusMeters: p.radiusMeters,
      isAvailable: p.isAvailable,
      meta: p.cooldownUntil ? `КД до ${new Date(p.cooldownUntil).toLocaleString("ru-RU")}` : undefined,
    })
  )

  return (
    <GameScreenLayout title="Боссы" backTo="/play/map">
      {!gps ? (
        <p className="mb-3 text-sm text-amber-200/90">Включите геолокацию для боя у точки.</p>
      ) : null}
      {!activeId ? (
        <p className="mb-3 text-sm text-amber-200">Выберите активную карту в колоде.</p>
      ) : null}

      {pointsQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : (
        <GeoPointsList
          points={points}
          gps={gps}
          highlightId={highlightId}
          renderActions={({ point, inRange }) => (
            <button
              type="button"
              disabled={!inRange || !activeId || fight.isPending}
              onClick={() => fight.mutate(point.id)}
              className="shrink-0 rounded-lg bg-guardians-accent px-3 py-1.5 text-xs font-medium text-guardians-bg disabled:opacity-40"
            >
              Бой
            </button>
          )}
        />
      )}

      {fight.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(fight.error, "Бой не удался")}
        </p>
      ) : null}

      {fight.data ? (
        <div className="mt-4 rounded-xl border border-white/10 p-3 text-sm">
          <p className="font-medium">{fight.data.bossName}</p>
          <p>Исход: {fight.data.outcome}</p>
          {fight.data.rewardCards.length ? (
            <p className="text-guardians-accent">
              Карт: {fight.data.rewardCards.length}
            </p>
          ) : null}
        </div>
      ) : null}
    </GameScreenLayout>
  )
}
